<template>
  <Admin style="background: #f2f2f2">
    <v-main>

      <!-- Unified Response Dialog -->
      <v-dialog v-model="showResponseDialog" max-width="900px">
        <v-card class="rounded-lg">
          <v-card-title class="d-flex align-center justify-space-between pa-4">
            <span class="text-h6 font-weight-bold">{{ responseDialogTitle }}</span>
            <div class="d-flex align-center">
              <v-btn
                v-if="responseDialogContent"
                icon
                small
                @click="copyToClipboard(responseDialogContent)"
                title="Copy to clipboard"
              >
                <v-icon small>mdi-content-copy</v-icon>
              </v-btn>
              <v-fade-transition>
                <span v-if="showCopiedMsg" class="text-caption success--text ml-1">Copied</span>
              </v-fade-transition>
              <v-btn icon small class="ml-2" @click="showResponseDialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-4" style="max-height: 65vh; overflow: auto;">
            <pre class="response-pre">{{ responseDialogContent }}</pre>
            <div v-if="responseDialogMeta" class="mt-4 pt-3" style="border-top: 1px solid #eee;">
              <v-chip
                v-for="(val, key) in responseDialogMeta"
                :key="key"
                small
                outlined
                class="mr-2 mb-1"
              >
                {{ key }}: {{ val }}
              </v-chip>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-container>
        <v-card class="rounded-xl" v-if="!isProductListLoading">
          <v-card-text v-if="!selectedCompany.isDemo">

            <!-- Breadcrumbs -->
            <v-breadcrumbs :items="breadcrumbItems" class="pa-0 mb-2">
              <template v-slot:divider>
                <v-icon small>mdi-chevron-right</v-icon>
              </template>
              <template v-slot:item="{ item }">
                <v-breadcrumbs-item :to="item.to" :disabled="item.disabled" :exact="true">
                  {{ item.text }}
                </v-breadcrumbs-item>
              </template>
            </v-breadcrumbs>

            <!-- Main Layout: PDF Preview + Controls -->
            <v-row dense>
              <!-- Left: PDF Upload & Preview -->
              <v-col cols="12" md="5" class="d-flex flex-column">
                <v-card
                  outlined
                  class="upload-card d-flex flex-column flex-grow-1 rounded-lg"
                  :class="{ 'upload-dragging': isDragging }"
                  @dragover.prevent="isDragging = true"
                  @dragleave.prevent="isDragging = false"
                  @drop.prevent="onFileDrop"
                >
                  <input
                    ref="fileInput"
                    type="file"
                    accept="application/pdf"
                    class="d-none"
                    @change="onFileSelected"
                  />

                  <!-- Upload Placeholder -->
                  <div
                    v-if="!pdfUrl"
                    class="upload-placeholder flex-grow-1 d-flex flex-column justify-center align-center tw-cursor-pointer pa-6"
                    @click="triggerFileSelect"
                  >
                    <v-icon size="56" color="grey lighten-1">mdi-cloud-upload-outline</v-icon>
                    <p class="text-body-1 font-weight-medium mt-3 mb-1 grey--text text--darken-1">
                      Drag & drop PDF here
                    </p>
                    <p class="text-caption grey--text">
                      or <span class="primary--text font-weight-medium">browse files</span>
                    </p>
                  </div>

                  <!-- PDF Preview -->
                  <template v-else>
                    <div class="d-flex align-center pa-3 upload-file-bar">
                      <v-icon small class="mr-2" color="red">mdi-file-pdf-box</v-icon>
                      <span class="text-body-2 font-weight-medium text-truncate flex-grow-1">{{ fileName }}</span>
                      <v-btn icon x-small @click="clearFile" title="Remove file">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                    <v-divider />
                    <v-responsive class="pdf-full flex-grow-1">
                      <iframe
                        :src="pdfUrl + '#toolbar=0&navpanes=0&scrollbar=0'"
                        frameborder="0"
                        class="w-100 h-100"
                      />
                    </v-responsive>
                  </template>
                </v-card>
              </v-col>

              <!-- Right: Tabs + Controls -->
              <v-col cols="12" md="7">
                <v-card outlined class="rounded-lg">

                  <!-- Tab Header -->
                  <v-tabs v-model="activeTab" background-color="transparent" color="primary" grow>
                    <v-tab>
                      <v-icon left small>mdi-text-recognition</v-icon>
                      OCR + AI
                    </v-tab>
                    <v-tab>
                      <v-icon left small>mdi-lightning-bolt</v-icon>
                      Direct AI
                    </v-tab>
                  </v-tabs>
                  <v-divider />

                  <!-- Error Alert (shared) -->
                  <v-alert
                    v-if="currentError"
                    type="error"
                    dense
                    dismissible
                    class="ma-4 mb-0 rounded-lg"
                    @input="dismissError"
                  >
                    {{ currentError }}
                  </v-alert>

                  <!-- Tab 1: OCR + AI -->
                  <div v-if="activeTab == 0" class="pa-4">
                    <!-- OCR Service Selection -->
                    <div class="d-flex align-center mb-3">
                      <span class="text-caption grey--text mr-3 font-weight-medium">OCR ENGINE</span>
                      <v-chip-group v-model="selectedServiceIndex" mandatory active-class="primary--text">
                        <v-chip small outlined filter>
                          <v-icon left x-small>mdi-image-search</v-icon>
                          Google Vision
                        </v-chip>
                        <v-chip small outlined filter>
                          <v-icon left x-small>mdi-text-box-search</v-icon>
                          Python (Text)
                        </v-chip>
                      </v-chip-group>
                    </div>

                    <!-- Model Select -->
                    <v-select
                      v-model="selectedModel"
                      :items="models"
                      return-object
                      label="AI Model"
                      outlined
                      dense
                      hide-details
                      class="mb-3"
                    >
                      <template v-slot:item="{ item }">
                        <v-row align="center" no-gutters>
                          <v-avatar size="18" class="mr-2">
                            <img :src="`/images/${item.image}`" alt="">
                          </v-avatar>
                          <span>{{ item.label }}</span>
                          <v-chip x-small class="ml-2" outlined>{{ item.provider }}</v-chip>
                        </v-row>
                      </template>
                      <template v-slot:selection="{ item }">
                        <v-row align="center" no-gutters>
                          <v-avatar size="18" class="mr-2">
                            <img :src="`/images/${item.image}`" alt="">
                          </v-avatar>
                          <span>{{ item.label }}</span>
                        </v-row>
                      </template>
                    </v-select>

                    <!-- Prompt -->
                    <v-textarea
                      v-model="prompt"
                      label="Enter your prompt..."
                      outlined
                      rows="8"
                      no-resize
                      hide-details
                      class="mb-3"
                    />

                    <!-- Action Bar -->
                    <div class="d-flex align-center">
                      <v-btn
                        color="success"
                        :disabled="!pdfUrl || !selectedModel || !prompt || isProcessing"
                        :loading="isProcessing"
                        @click="processPdf"
                        class="mr-2"
                      >
                        <v-icon left small>mdi-play</v-icon>
                        Process
                      </v-btn>
                      <v-btn
                        outlined
                        color="error"
                        :disabled="!pdfUrl || isProcessing"
                        @click="clearOcr"
                        class="mr-2"
                      >
                        <v-icon left small>mdi-trash-can-outline</v-icon>
                        Clear
                      </v-btn>
                      <v-spacer />
                      <v-btn
                        icon
                        :disabled="!rawText || isProcessing"
                        @click="openResponseDialog('Extracted Text', rawText, null)"
                        title="View extracted text"
                      >
                        <v-icon>mdi-format-text</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        :disabled="!jsonResult || isProcessing"
                        @click="openResponseDialog('OCR + AI Response', rawText + '\n\n' + formatJson(jsonResult), { Service: service, Model: selectedModel.label })"
                        title="View full response"
                      >
                        <v-icon>mdi-file-search-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <!-- Tab 2: Direct AI -->
                  <div v-if="activeTab == 1" class="pa-4">
                    <!-- Mode Toggle -->
                    <div class="d-flex align-center mb-3">
                      <span class="text-caption grey--text mr-3 font-weight-medium">MODE</span>
                      <v-chip-group v-model="directModeIndex" mandatory active-class="primary--text">
                        <v-chip small outlined filter>
                          <v-icon left x-small>mdi-file-pdf-box</v-icon>
                          PDF Direct
                        </v-chip>
                        <v-chip small outlined filter>
                          <v-icon left x-small>mdi-image-multiple</v-icon>
                          PDF to Images
                        </v-chip>
                      </v-chip-group>
                    </div>

                    <!-- Model Select -->
                    <v-select
                      v-model="directModel"
                      :items="directModels"
                      return-object
                      label="AI Model"
                      outlined
                      dense
                      hide-details
                      class="mb-3"
                    >
                      <template v-slot:item="{ item }">
                        <v-row align="center" no-gutters>
                          <v-avatar size="18" class="mr-2">
                            <img :src="`/images/${item.image}`" alt="">
                          </v-avatar>
                          <span>{{ item.label }}</span>
                          <v-chip x-small class="ml-2" outlined>{{ item.provider }}</v-chip>
                        </v-row>
                      </template>
                      <template v-slot:selection="{ item }">
                        <v-row align="center" no-gutters>
                          <v-avatar size="18" class="mr-2">
                            <img :src="`/images/${item.image}`" alt="">
                          </v-avatar>
                          <span>{{ item.label }}</span>
                        </v-row>
                      </template>
                    </v-select>

                    <!-- Prompt -->
                    <v-textarea
                      v-model="directPrompt"
                      label="Enter your prompt..."
                      outlined
                      rows="8"
                      no-resize
                      hide-details
                      class="mb-3"
                    />

                    <!-- Action Bar -->
                    <div class="d-flex align-center">
                      <v-btn
                        color="deep-purple"
                        :disabled="!pdfUrl || !directModel || !directPrompt || isDirectProcessing"
                        :loading="isDirectProcessing"
                        @click="processDirectPdf"
                        class="mr-2"
                      >
                        <v-icon left small>mdi-lightning-bolt</v-icon>
                        Direct AI
                        <span v-if="isDirectProcessing" class="ml-2">{{ directElapsed }}s</span>
                      </v-btn>
                      <v-btn
                        outlined
                        color="error"
                        :disabled="isDirectProcessing"
                        @click="clearDirect"
                        class="mr-2"
                      >
                        <v-icon left small>mdi-trash-can-outline</v-icon>
                        Clear
                      </v-btn>
                      <v-spacer />
                      <v-btn
                        icon
                        :disabled="!directJsonResult || directHasError"
                        @click="openResponseDialog('Direct AI Response', formatJson(directJsonResult), directInfo)"
                        title="View response"
                      >
                        <v-icon>mdi-file-search-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- JSON Result Section -->
            <v-card outlined class="rounded-lg mt-4">
              <div class="d-flex align-center pa-4 pb-2">
                <v-icon small class="mr-2" color="primary">mdi-code-json</v-icon>
                <span class="text-subtitle-1 font-weight-bold">JSON Result</span>
                <v-spacer />
                <!-- Meta info chips -->
                <template v-if="activeTab == 0 && jsonResult">
                  <v-chip v-if="service" x-small outlined class="mr-2">{{ service }}</v-chip>
                  <v-chip v-if="selectedModel && selectedModel.label" x-small outlined>{{ selectedModel.label }}</v-chip>
                </template>
                <template v-if="activeTab == 1 && directInfo">
                  <v-chip v-if="directInfo.mode" x-small outlined class="mr-2">{{ directInfo.mode }}</v-chip>
                  <v-chip v-if="directInfo.model" x-small outlined class="mr-2">{{ directInfo.model }}</v-chip>
                  <v-chip v-if="directInfo.tokenInfo" x-small outlined class="mr-2">{{ directInfo.tokenInfo }} tokens</v-chip>
                  <v-chip v-if="directInfo.elapsed" x-small outlined>{{ directInfo.elapsed }}s</v-chip>
                </template>
                <!-- Copy button -->
                <v-btn
                  v-if="currentJsonResult && !currentHasError"
                  small
                  outlined
                  color="primary"
                  class="ml-2"
                  @click="copyToClipboard(formatJson(currentJsonResult))"
                >
                  <v-icon left small>mdi-content-copy</v-icon>
                  Copy
                </v-btn>
                <v-fade-transition>
                  <span v-if="showCopiedMsg" class="text-caption success--text ml-2">Copied!</span>
                </v-fade-transition>
              </div>
              <v-divider />
              <div class="result-container pa-4">
                <pre v-if="currentJsonResult && !currentHasError" class="response-pre">{{ formatJson(currentJsonResult) }}</pre>
                <div v-else class="d-flex flex-column align-center justify-center py-12">
                  <v-icon size="48" color="grey lighten-2">mdi-code-json</v-icon>
                  <p class="text-body-2 grey--text mt-3">
                    {{ activeTab == 0
                      ? 'Upload a PDF, select a model and enter a prompt, then click "Process"'
                      : 'Upload a PDF, select a model and enter a prompt, then click "Direct AI"'
                    }}
                  </p>
                </div>
              </div>
            </v-card>

          </v-card-text>

          <!-- No Permission -->
          <div
            v-else
            class="d-flex align-center justify-center"
            style="height: 200px"
          >
            <div class="text-center">
              <v-icon size="48" color="grey lighten-2">mdi-lock-outline</v-icon>
              <p class="text-body-1 grey--text mt-2">No Permission</p>
            </div>
          </div>
        </v-card>

        <!-- Loading Skeleton -->
        <v-card v-else class="rounded-xl pa-7">
          <v-skeleton-loader type="list-item-two-line" style="width: 400px;" />
          <v-skeleton-loader type="list-item-three-line" style="width: 400px;" />
          <v-skeleton-loader type="list-item-three-line" style="width: 400px;" />
        </v-card>
      </v-container>
    </v-main>
  </Admin>
</template>

<script>
import Admin from "../../components/admin/admin-main-template";
import { mapState } from "vuex";
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/functions";

export default {
  name: "SettingTestDoc",
  components: {
    Admin,
  },

  watch: {
    directModeIndex() {
      var self = this;
      var defaultModel = { label: 'Gemini Flash 2.5', value: 'gemini-2.5-flash', provider: 'Google', image: 'gg.png' };
      if (!self.directModel) {
        self.directModel = defaultModel;
        return;
      }
      var found = self.directModels.some(function(m) { return m.value === self.directModel.value; });
      if (!found) {
        self.directModel = defaultModel;
      }
    },
  },

  computed: {
    ...mapState({}),

    selectedService() {
      return this.selectedServiceIndex === 0 ? 'Google' : 'SLD';
    },

    directMode() {
      return this.directModeIndex === 0 ? 'pdf_direct' : 'pdf_to_images';
    },

    directModels() {
      var allModels = [
        { label: 'Gemini Flash 2.5', value: 'gemini-2.5-flash', provider: 'Google', image: 'gg.png' },
        { label: 'Gemini Pro 2.5', value: 'gemini-2.5-pro', provider: 'Google', image: 'gg.png' },
        { label: 'Gemini Flash 3.5', value: 'gemini-3.5-flash', provider: 'Google', image: 'gg.png' },
        { label: 'Claude Haiku 3.5', value: 'claude-3-5-haiku-latest', provider: 'Claude', image: 'claude.png' },
        { label: 'GPT-4.1 mini', value: 'gpt-4.1-mini', provider: 'OpenAI', image: 'openai.png' },
        { label: 'GPT-4.1', value: 'gpt-4.1', provider: 'OpenAI', image: 'openai.png' },
        { label: 'o3', value: 'o3', provider: 'OpenAI', image: 'openai.png' },
      ];
      if (this.directMode === 'pdf_direct') {
        return allModels.filter(function(m) { return m.provider === 'Google' || m.provider === 'Claude'; });
      }
      return allModels;
    },

    currentJsonResult() {
      return this.activeTab == 0 ? this.jsonResult : this.directJsonResult;
    },

    currentHasError() {
      return this.activeTab == 1 && this.directHasError;
    },

    currentError() {
      return this.activeTab == 1 ? this.directError : this.ocrError;
    },
  },

  data: function() {
    return {
      activeTab: 0,
      isDragging: false,

      breadcrumbItems: [
        { text: "ตั้งค่า", disabled: false, to: { name: "Company.Settings" } },
        { text: "ทดสอบเอกสาร", disabled: true },
      ],

      // Shared
      fileName: '',
      pdfUrl: null,
      isProductListLoading: false,
      selectedCompany: "",
      showCopiedMsg: false,

      // Response dialog
      showResponseDialog: false,
      responseDialogTitle: '',
      responseDialogContent: '',
      responseDialogMeta: null,

      // OCR + AI
      models: [
        { label: 'Gemini Flash 2.5', value: 'gemini-2.5-flash', provider: 'Google', image: 'gg.png' },
        { label: 'DeepSeek Chat', value: 'deepseek-chat', provider: 'Deepseek', image: 'deepseek.png' },
        { label: 'Claude Haiku 3.5', value: 'claude-3-5-haiku-latest', provider: 'Claude', image: 'claude.png' },
        { label: 'GPT-4.1 mini', value: 'gpt-4.1-mini', provider: 'OpenAI', image: 'openai.png' },
        { label: 'Gemini Pro 2.5', value: 'gemini-2.5-pro', provider: 'Google', image: 'gg.png' },
        { label: 'o3', value: 'o3', provider: 'OpenAI', image: 'openai.png' },
        { label: 'GPT-4.1', value: 'gpt-4.1', provider: 'OpenAI', image: 'openai.png' },
      ],
      selectedModel: null,
      selectedServiceIndex: 0,
      prompt: '',
      jsonResult: '',
      rawText: '',
      service: '',
      isProcessing: false,
      ocrError: '',

      // Direct AI
      directModeIndex: 0,
      directModel: { label: 'Gemini Flash 2.5', value: 'gemini-2.5-flash', provider: 'Google', image: 'gg.png' },
      directPrompt: '',
      directJsonResult: '',
      isDirectProcessing: false,
      directInfo: null,
      directError: '',
      directElapsed: 0,
      directTimer: null,
      directHasError: false,
    };
  },

  created() {
    this.selectedModel = this.models[0];
  },

  beforeDestroy() {
    if (this.pdfUrl) URL.revokeObjectURL(this.pdfUrl);
    if (this.directTimer) clearInterval(this.directTimer);
  },

  methods: {
    triggerFileSelect() {
      this.$refs.fileInput.click();
    },

    onFileSelected(e) {
      var file = e.target.files[0];
      if (!file) {
        this.clearFile();
        return;
      }
      this.fileName = file.name;
      if (this.pdfUrl) URL.revokeObjectURL(this.pdfUrl);
      this.pdfUrl = URL.createObjectURL(file);
    },

    onFileDrop(e) {
      this.isDragging = false;
      var files = e.dataTransfer.files;
      if (!files || !files.length) return;
      var file = files[0];
      if (file.type !== 'application/pdf') return;
      this.fileName = file.name;
      if (this.pdfUrl) URL.revokeObjectURL(this.pdfUrl);
      this.pdfUrl = URL.createObjectURL(file);

      // Update file input so processPdf can read from it
      var dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      this.$refs.fileInput.files = dataTransfer.files;
    },

    clearFile() {
      if (this.pdfUrl) URL.revokeObjectURL(this.pdfUrl);
      this.pdfUrl = null;
      this.fileName = '';
      this.$refs.fileInput.value = '';
    },

    copyToClipboard(content) {
      var self = this;
      try {
        navigator.clipboard.writeText(content);
        self.showCopiedMsg = true;
        setTimeout(function() {
          self.showCopiedMsg = false;
        }, 3000);
      } catch (err) {
        console.error("Copy failed:", err);
      }
    },

    dismissError() {
      if (this.activeTab == 1) {
        this.directError = '';
      } else {
        this.ocrError = '';
      }
    },

    openResponseDialog(title, content, meta) {
      this.responseDialogTitle = title;
      this.responseDialogContent = content;
      this.responseDialogMeta = meta;
      this.showResponseDialog = true;
    },

    formatJson(raw) {
      try {
        return JSON.stringify(JSON.parse(raw), null, 2);
      } catch (e) {
        return raw;
      }
    },

    // === OCR + AI ===
    async processPdf() {
      if (!this.pdfUrl) return;
      this.jsonResult = '';
      this.rawText = '';
      this.service = '';
      this.ocrError = '';
      this.isProcessing = true;

      var self = this;
      var file = this.$refs.fileInput.files[0];
      var reader = new FileReader();
      reader.onload = async function() {
        try {
          var base64 = reader.result.split(',')[1];
          var callTextPdfTest = firebase.functions().httpsCallable("callTextPdfTest");
          var payload = {
            pdfBuffer: base64,
            prompt: self.prompt,
            model: self.selectedModel.value || 'gemini-2.5-flash',
            provider: self.selectedModel.provider || 'Google',
            service: self.selectedService,
          };
          var result = await callTextPdfTest(payload);
          self.jsonResult = result.data.jsonResult;
          self.rawText = result.data.rawText;
          self.service = result.data.service;
        } catch (err) {
          console.error("Error calling function:", err);
          self.ocrError = err.message || String(err);
        } finally {
          self.isProcessing = false;
        }
      };
      reader.readAsDataURL(file);
    },

    clearOcr() {
      this.jsonResult = '';
      this.rawText = '';
      this.service = '';
      this.ocrError = '';
      this.prompt = '';
      this.selectedModel = this.models[0];
      this.selectedServiceIndex = 0;
    },

    // === Direct AI ===
    async processDirectPdf() {
      if (!this.pdfUrl) return;
      var file = this.$refs.fileInput.files[0];
      if (!file) return;

      this.directJsonResult = '';
      this.directInfo = null;
      this.directError = '';
      this.directHasError = false;
      this.isDirectProcessing = true;
      this.directElapsed = 0;

      var self = this;
      this.directTimer = setInterval(function() {
        self.directElapsed++;
      }, 1000);

      try {
        var base64 = await this.readFileAsBase64(file);
        var callDirectPdfTest = firebase.functions().httpsCallable("callDirectPdfTest");
        var payload = {
          pdfBuffer: base64,
          prompt: this.directPrompt,
          model: this.directModel.value,
          provider: this.directModel.provider,
          mode: this.directMode,
        };

        var result = await callDirectPdfTest(payload);
        this.directJsonResult = result.data.jsonResult;
        this.directInfo = {
          mode: result.data.mode,
          model: result.data.model,
          tokenInfo: result.data.tokenInfo || '',
          elapsed: this.directElapsed,
        };
      } catch (err) {
        console.error("Error calling direct function:", err);
        this.directError = err.message || String(err);
        this.directHasError = true;
      } finally {
        clearInterval(this.directTimer);
        this.directTimer = null;
        this.isDirectProcessing = false;
      }
    },

    readFileAsBase64(file) {
      return new Promise(function(resolve, reject) {
        var reader = new FileReader();
        reader.onload = function() {
          resolve(reader.result.split(',')[1]);
        };
        reader.onerror = function() {
          reject(new Error('Failed to read file'));
        };
        reader.readAsDataURL(file);
      });
    },

    clearDirect() {
      this.directJsonResult = '';
      this.directInfo = null;
      this.directError = '';
      this.directHasError = false;
      this.directModeIndex = 0;
      this.directModel = { label: 'Gemini Flash 2.5', value: 'gemini-2.5-flash', provider: 'Google', image: 'gg.png' };
      this.directPrompt = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.upload-card {
  min-height: 500px;
  border: 2px dashed #e0e0e0 !important;
  background: #fafafa;
  transition: all 0.2s ease;

  &.upload-dragging {
    border-color: #1976d2 !important;
    background: #e3f2fd;
  }
}

.upload-placeholder:hover {
  background: #f0f0f0;
  border-radius: 8px;
}

.upload-file-bar {
  background: #f5f5f5;
}

.pdf-full {
  flex: 1 1 auto;
  position: relative;
  min-height: 400px;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
}

.result-container {
  max-height: 500px;
  overflow: auto;
  background: #fafafa;
}

.response-pre {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: 'Fira Code', 'Consolas', monospace;
  font-size: 13px;
  line-height: 1.6;
  margin: 0;
  color: #333;
}

::v-deep .v-tab {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0;
}

</style>
